import React, { useState, useEffect } from 'react';
import { simulationService } from '../../services/simulationService';
import { Trash2, RefreshCw, AlertTriangle, Loader2, Edit, Plus } from 'lucide-react';
import LayerAreasModal from '../modals/LayerAreasModal';
import CreateProjectModal from '../modals/CreateProjectModal';
import EditProjectModal from '../modals/EditProjectModal';
import QuantitySourceToggle from '../common/QuantitySourceToggle';

export const ProjectManagement = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState({ show: false, projectId: null });
  const [deleteStatus, setDeleteStatus] = useState({ success: false, message: '', projectId: null });
  const [isDeleting, setIsDeleting] = useState(false);
  const [editModal, setEditModal] = useState({ show: false, projectId: null });
  const [renameStatus, setRenameStatus] = useState({ success: false, message: '', projectId: null });
  const [layerAreasModal, setLayerAreasModal] = useState({ show: false, projectId: null, data: null });
  const [layerAreasStatus, setLayerAreasStatus] = useState({ success: false, message: '', projectId: null });
  const [quantitySources, setQuantitySources] = useState({});
  const [quantitySourceLoading, setQuantitySourceLoading] = useState({});
  const [hasLayerAreas, setHasLayerAreas] = useState({});
  const [pendingSourceSwitch, setPendingSourceSwitch] = useState(null);
  const [createProjectModal, setCreateProjectModal] = useState({ show: false });
  const [createProjectStatus, setCreateProjectStatus] = useState({ success: false, message: '', projectName: null });

  const fetchProjects = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await simulationService.getProjects();
      setProjects(response.data || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
      setError('Failed to load projects. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProjects();
  }, []);

  // Fetch quantity sources when projects are loaded
  useEffect(() => {
    if (projects.length > 0) {
      projects.forEach(project => {
        fetchQuantitySource(project);
      });
    }
  }, [projects]);

  const handleDeleteClick = (projectId) => {
    setDeleteConfirmation({ show: true, projectId });
  };

  const handleCancelDelete = () => {
    setDeleteConfirmation({ show: false, projectId: null });
  };

  const handleConfirmDelete = async () => {
    try {
      setIsDeleting(true);
      const projectId = deleteConfirmation.projectId;
      const response = await simulationService.deleteProject(projectId);

      if (response.success) {
        setDeleteStatus({
          success: true,
          message: `Projekt ${projectId} wurde erfolgreich gelöscht.`,
          projectId
        });
        // Remove the deleted project from the list
        setProjects(projects.filter(p => p !== projectId));

        // Set flag to force dashboard refresh for when user visits dashboard later
        localStorage.setItem('dashboardNeedsRefresh', 'true');
      } else {
        setDeleteStatus({
          success: false,
          message: response.error || 'Fehler beim Löschen des Projekts.',
          projectId
        });
      }
    } catch (error) {
      console.error('Error deleting project:', error);
      setDeleteStatus({
        success: false,
        message: error.message || 'Fehler beim Löschen des Projekts.',
        projectId: deleteConfirmation.projectId
      });
    } finally {
      setIsDeleting(false);
      setDeleteConfirmation({ show: false, projectId: null });
      // Clear status message after 5 seconds
      setTimeout(() => {
        setDeleteStatus({ success: false, message: '', projectId: null });
      }, 5000);
    }
  };

  const handleEditClick = (projectId) => {
    setEditModal({ show: true, projectId });
  };

  const handleCloseEditModal = () => {
    setEditModal({ show: false, projectId: null });
  };

  const handleProjectSaved = ({ previousId, newId }) => {
    const renamed = previousId !== newId;
    setRenameStatus({
      success: true,
      message: renamed
        ? `Projekt ${previousId} wurde erfolgreich aktualisiert und in ${newId} umbenannt.`
        : `Projekt ${newId} wurde erfolgreich aktualisiert.`,
      projectId: newId
    });

    if (renamed) {
      setProjects(prevProjects => {
        const updated = [...prevProjects];
        const idx = updated.indexOf(previousId);
        if (idx !== -1) updated[idx] = newId;
        return updated;
      });
      // Migrate any per-project state keyed by the old ID
      setQuantitySources(prev => {
        if (!(previousId in prev)) return prev;
        const { [previousId]: value, ...rest } = prev;
        return { ...rest, [newId]: value };
      });
    }

    localStorage.setItem('dashboardNeedsRefresh', 'true');

    setTimeout(() => {
      setRenameStatus({ success: false, message: '', projectId: null });
    }, 5000);
  };

  const handleEditLayerAreas = async (projectId) => {
    try {
      // Try to fetch existing layer areas data
      const response = await simulationService.getLayerAreas(projectId);
      const existingData = response.success ? response.data : null;

      setLayerAreasModal({
        show: true,
        projectId,
        data: existingData
      });
    } catch (error) {
      console.error('Error fetching layer areas:', error);
      // Still open modal even if fetch fails (for new entries)
      setLayerAreasModal({
        show: true,
        projectId,
        data: null
      });
    }
  };

  const handleSaveLayerAreas = async (layerAreasData) => {
    const projectId = layerAreasModal.projectId;
    // If the modal was opened by clicking "Flächen" without existing data,
    // ensure the save persists that switch even if the modal didn't surface it.
    const payload =
      pendingSourceSwitch === projectId
        ? { ...layerAreasData, quantitySource: 'calculatedLayers' }
        : layerAreasData;

    try {
      const response = await simulationService.saveLayerAreas(projectId, payload);

      if (response.success) {
        setLayerAreasStatus({
          success: true,
          message: `Schichtflächen für Projekt ${projectId} wurden erfolgreich gespeichert.`,
          projectId
        });

        // Layer areas now exist for this project
        setHasLayerAreas(prev => ({ ...prev, [projectId]: true }));

        // Update quantity source in local state if it was included in the save
        if (payload.quantitySource) {
          setQuantitySources(prev => ({
            ...prev,
            [projectId]: payload.quantitySource
          }));
        }
      } else {
        setLayerAreasStatus({
          success: false,
          message: response.error || 'Fehler beim Speichern der Schichtflächen.',
          projectId: layerAreasModal.projectId
        });
      }
    } catch (error) {
      console.error('Error saving layer areas:', error);
      setLayerAreasStatus({
        success: false,
        message: error.message || 'Fehler beim Speichern der Schichtflächen.',
        projectId: layerAreasModal.projectId
      });
    } finally {
      // Always clear pending switch intent once a save attempt completes
      setPendingSourceSwitch(null);
      // Clear status message after 5 seconds
      setTimeout(() => {
        setLayerAreasStatus({ success: false, message: '', projectId: null });
      }, 5000);
    }
  };

  // Fetch quantity source for a project
  const fetchQuantitySource = async (projectId) => {
    try {
      setQuantitySourceLoading(prev => ({ ...prev, [projectId]: true }));
      const response = await simulationService.getQuantitySource(projectId);

      if (response.success) {
        setQuantitySources(prev => ({
          ...prev,
          [projectId]: response.data.quantitySource
        }));
        setHasLayerAreas(prev => ({
          ...prev,
          [projectId]: !!response.data.hasLayerAreas
        }));
      }
    } catch (error) {
      console.error('Error fetching quantity source:', error);
      // Set default if error - assume no layer areas exist yet
      setQuantitySources(prev => ({
        ...prev,
        [projectId]: 'deliveryNotes'
      }));
      setHasLayerAreas(prev => ({
        ...prev,
        [projectId]: false
      }));
    } finally {
      setQuantitySourceLoading(prev => ({ ...prev, [projectId]: false }));
    }
  };

  // Handle quantity source toggle
  const handleQuantitySourceChange = async (projectId, newSource) => {
    const currentSource = quantitySources[projectId] || 'deliveryNotes';

    // Re-clicking the already-active Flächen pill opens the layer areas
    // modal in edit mode so the user can update areas and quantities.
    if (
      newSource === 'calculatedLayers' &&
      currentSource === 'calculatedLayers' &&
      hasLayerAreas[projectId]
    ) {
      handleEditLayerAreas(projectId);
      return;
    }

    // Re-clicking the already-active option in any other case is a no-op.
    if (newSource === currentSource) return;

    // Switching to "Flächen" requires layer-area data. If none exists yet,
    // open the layer areas modal so the user can enter it; the switch is
    // applied automatically once they save.
    if (newSource === 'calculatedLayers' && !hasLayerAreas[projectId]) {
      setPendingSourceSwitch(projectId);
      setLayerAreasModal({
        show: true,
        projectId,
        data: { quantitySource: 'calculatedLayers' }
      });
      return;
    }

    try {
      setQuantitySourceLoading(prev => ({ ...prev, [projectId]: true }));
      const response = await simulationService.updateQuantitySource(projectId, newSource);

      if (response.success) {
        setQuantitySources(prev => ({
          ...prev,
          [projectId]: newSource
        }));
      }
    } catch (error) {
      console.error('Error updating quantity source:', error);
      // Show error message or revert change
    } finally {
      setQuantitySourceLoading(prev => ({ ...prev, [projectId]: false }));
    }
  };

  const handleCloseLayerAreasModal = () => {
    setLayerAreasModal({ show: false, projectId: null, data: null });
    setPendingSourceSwitch(null);
  };

  const handleRefresh = () => {
    fetchProjects();
  };

  const handleCreateProject = () => {
    setCreateProjectModal({ show: true });
  };

  const handleCancelCreateProject = () => {
    setCreateProjectModal({ show: false });
  };

  const handleSaveProject = async (projectData) => {
    try {
      const response = await simulationService.createProject(projectData);

      if (response.success) {
        setCreateProjectStatus({
          success: true,
          message: `Projekt "${projectData.projectName}" wurde erfolgreich erstellt.`,
          projectName: projectData.projectName
        });

        // Add the new project to the list
        setProjects(prevProjects => [...prevProjects, projectData.projectName]);

        // Close the modal
        setCreateProjectModal({ show: false });

        // Set flag to force dashboard refresh for when user visits dashboard later
        localStorage.setItem('dashboardNeedsRefresh', 'true');
      } else {
        setCreateProjectStatus({
          success: false,
          message: response.error || 'Fehler beim Erstellen des Projekts.',
          projectName: null
        });
      }
    } catch (error) {
      console.error('Error creating project:', error);
      setCreateProjectStatus({
        success: false,
        message: error.message || 'Fehler beim Erstellen des Projekts.',
        projectName: null
      });
    } finally {
      // Clear status message after 5 seconds
      setTimeout(() => {
        setCreateProjectStatus({ success: false, message: '', projectName: null });
      }, 5000);
    }
  };

  if (loading && projects.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col">
      <div className="flex-shrink-0 p-8 pb-4">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-2xl font-semibold">Projektverwaltung</h1>
            <p className="text-gray-500 mt-1">Verwalten Sie Ihre Projekte und deren Daten</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleCreateProject}
              className="flex items-center gap-1 px-3 py-1 bg-green-600 text-white rounded-full hover:bg-green-700 transition-colors"
              disabled={loading}
            >
              <Plus className="h-4 w-4" />
              <span>Neues Projekt erstellen</span>
            </button>
            <button
              onClick={handleRefresh}
              className="p-2 hover:bg-gray-100 rounded-full flex items-center gap-1 text-green-600"
              disabled={loading}
              title="Daten aktualisieren"
            >
              <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
              <span className="text-sm">Aktualisieren</span>
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 text-red-600 p-4 rounded-md mb-6 flex items-center gap-2">
            <AlertTriangle className="h-5 w-5" />
            <span>{error}</span>
          </div>
        )}

        {/* Create Project Status Message */}
        {createProjectStatus.message && (
          <div className={`p-4 rounded-md mb-6 flex items-center gap-2 ${
            createProjectStatus.success
              ? 'bg-green-50 text-green-600'
              : 'bg-red-50 text-red-600'
          }`}>
            <AlertTriangle className="h-5 w-5" />
            <span>{createProjectStatus.message}</span>
          </div>
        )}

        {(deleteStatus.message || renameStatus.message) && (
          <div className={`${
            (deleteStatus.success || renameStatus.success) ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'
          } p-4 rounded-md mb-6`}>
            {deleteStatus.message || renameStatus.message}
          </div>
        )}
      </div>

      <div className="flex-1 px-8 pb-8 min-h-0">
        <div className="bg-white rounded-lg shadow h-full flex flex-col overflow-hidden">

          {projects.length === 0 ? (
            <div className="flex-1 flex items-center justify-center">
              <div className="text-center text-gray-500">
                Keine Projekte gefunden.
              </div>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto" style={{ maxHeight: '80vh' }}>
              <ul className="divide-y divide-gray-200">
                {projects.map((project) => (
              <li key={project} className="px-6 py-4 hover:bg-gray-50">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">{project}</h3>
                  </div>
                  <div className="flex gap-2">
                  <button
                    onClick={() => handleEditClick(project)}
                    className="flex items-center justify-center p-2 bg-blue-50 text-blue-600 rounded-full hover:bg-blue-100 transition-colors"
                    title="Bearbeiten"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteClick(project)}
                    className="flex items-center justify-center p-2 bg-red-50 text-red-600 rounded-full hover:bg-red-100 transition-colors"
                    title="Löschen"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                  </div>
                </div>

                {/* Quantity Source Toggle */}
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <QuantitySourceToggle
                    value={quantitySources[project] || 'deliveryNotes'}
                    onChange={(newSource) => handleQuantitySourceChange(project, newSource)}
                    disabled={quantitySourceLoading[project]}
                  />
                </div>
              </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirmation.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h2 className="text-xl font-semibold mb-4">Projekt löschen</h2>
            <p className="mb-6">
              Sind Sie sicher, dass Sie das Projekt <strong>{deleteConfirmation.projectId}</strong> löschen möchten?
              Diese Aktion kann nicht rückgängig gemacht werden und alle zugehörigen Daten werden gelöscht.
            </p>

            {isDeleting ? (
              <div className="flex flex-col items-center justify-center py-4 mb-6">
                <Loader2 className="h-8 w-8 animate-spin text-red-600 mb-2" />
                <p className="text-gray-700">Daten werden gelöscht...</p>
              </div>
            ) : (
              <div className="flex justify-end gap-4">
                <button
                  onClick={handleCancelDelete}
                  className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                  disabled={isDeleting}
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleConfirmDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                  disabled={isDeleting}
                >
                  Löschen
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Edit Project Modal */}
      <EditProjectModal
        isOpen={editModal.show}
        onClose={handleCloseEditModal}
        projectId={editModal.projectId}
        onSaved={handleProjectSaved}
      />

      {/* Status Messages */}
      {renameStatus.message && (
        <div className={`fixed top-4 right-4 p-4 rounded-md shadow-lg z-50 ${
          renameStatus.success ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'
        }`}>
          {renameStatus.message}
        </div>
      )}

      {layerAreasStatus.message && (
        <div className={`fixed top-16 right-4 p-4 rounded-md shadow-lg z-50 ${
          layerAreasStatus.success ? 'bg-green-50 text-green-600' : 'bg-red-50 text-red-600'
        }`}>
          {layerAreasStatus.message}
        </div>
      )}

      {/* Layer Areas Modal */}
      <LayerAreasModal
        isOpen={layerAreasModal.show}
        onClose={handleCloseLayerAreasModal}
        projectId={layerAreasModal.projectId}
        onSave={handleSaveLayerAreas}
        initialData={layerAreasModal.data}
      />

      {/* Create Project Modal */}
      <CreateProjectModal
        isOpen={createProjectModal.show}
        onClose={handleCancelCreateProject}
        onSave={handleSaveProject}
      />
    </div>
  );
};

export default ProjectManagement;
